import { useMemo } from "react";
import { Spotlight, type SpotlightActionData, type SpotlightActionGroupData } from "@mantine/spotlight";
import { useGo } from "./ConsoleShell";
import { PALETTE_COMMANDS } from "../nav";
import { useConsoleStore } from "../stores/consoleStore";
import { useUiStore } from "../stores/uiStore";
import css from "./palette.module.css";

/**
 * ⌘K command palette. Mantine's Spotlight already owns the shortcut, the
 * overlay and the filtering, so this only supplies the two action groups the
 * mockup shows: COMMANDS and JUMP TO NAMESPACE.
 */
export function CommandPalette() {
  const go = useGo();
  const namespaces = useConsoleStore((s) => s.namespaces);
  const openDetail = useUiStore((s) => s.openDetail);

  const actions = useMemo<(SpotlightActionGroupData | SpotlightActionData)[]>(
    () => [
      {
        group: "COMMANDS",
        actions: PALETTE_COMMANDS.map((c) => ({
          id: c.to,
          label: c.label,
          // The mockup marks commands with ▸ and shows the shortcut on the right.
          leftSection: <span className={css.bullet}>▸</span>,
          rightSection: <span className={css.key}>{c.key}</span>,
          onClick: () => go(c.to),
        })),
      },
      {
        group: "JUMP TO NAMESPACE",
        actions: namespaces.map((n) => ({
          id: `ns:${n.id}`,
          label: n.label,
          leftSection: <span className={css.hash}>#</span>,
          onClick: () => {
            go("/namespaces");
            openDetail("namespace", n.id);
          },
        })),
      },
    ],
    [go, namespaces, openDetail],
  );

  return (
    <Spotlight
      actions={actions}
      shortcut={["mod + K"]}
      nothingFound="no match"
      highlightQuery
      searchProps={{
        placeholder: "type a command or namespace…",
        leftSection: <span className={css.prompt}>$</span>,
      }}
      overlayProps={{ backgroundOpacity: 0.6, color: "#050503" }}
      classNames={{
        content: css.content,
        search: css.search,
        actionsGroup: css.group,
        action: css.action,
        actionLabel: css.actionLabel,
      }}
    />
  );
}
